import type { JSX } from 'solid-js';
import { BottomSheet } from '../components/BottomSheet';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Popover } from '../components/Popover';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { createToast } from '../lib/toast';

// Storybook-style showcase for the three dashboard overlay primitives
// (Popover, BottomSheet, Toast) — one Panel per primitive, each with a short
// usage note and the primitive mounted live so an adopter can see what each
// one does without spelunking through its importers list. Group is null
// (no rail row) and the page is not bleed; the ORG_ROUTE_TABLE entry in
// `lib/routes.ts` owns those decisions so AppShell renders this inside the
// normal `.page` rhythm. Mounted at `/:org/primitives-demo` under the org
// scope — the rail, the command palette and the topbar breadcrumb all read
// the label through `t('nav.primitives_demo')`.
//
// Every user-facing string goes through `t()` against the `primitives_demo.*`
// namespace (locales/en.json + zh.json) — the i18n coverage contract can only
// flag keys, never literals. The three Panel titles stay the components' own
// names: they are code tokens, kept in English in zh like PR/BYOK/MCP.

export function PrimitivesDemo(): JSX.Element {
  const { toast, notify, dismiss } = createToast();
  return (
    <>
      <PageHeader title={t('primitives_demo.title')} desc={t('primitives_demo.desc')} />
      <Panel title={t('primitives_demo.popover_title')}>
        <p>{t('primitives_demo.popover_usage')}</p>
        <Popover
          label={t('primitives_demo.popover_trigger')}
          trigger={t('primitives_demo.popover_trigger')}
        >
          {(close) => (
            <div>
              <p>{t('primitives_demo.popover_body')}</p>
              <button type="button" onClick={close}>
                {t('primitives_demo.popover_close')}
              </button>
            </div>
          )}
        </Popover>
      </Panel>
      <Panel title={t('primitives_demo.sheet_title')}>
        <p>{t('primitives_demo.sheet_usage')}</p>
        <BottomSheet
          label={t('primitives_demo.sheet_trigger')}
          trigger={t('primitives_demo.sheet_trigger')}
          title={t('primitives_demo.sheet_dialog_title')}
        >
          {(close) => (
            <div>
              <p>{t('primitives_demo.sheet_body')}</p>
              <Button onClick={close}>{t('primitives_demo.sheet_done')}</Button>
            </div>
          )}
        </BottomSheet>
      </Panel>
      <Panel title={t('primitives_demo.toast_title')}>
        <p>{t('primitives_demo.toast_usage')}</p>
        <Button onClick={() => notify(t('primitives_demo.toast_fired'))}>
          {t('primitives_demo.toast_show')}
        </Button>
        <Toast
          message={toast()?.message ?? null}
          tone={toast()?.tone ?? 'success'}
          onDismiss={dismiss}
        />
      </Panel>
    </>
  );
}
